import React, { useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, UsersIcon, AlertTriangleIcon, DownloadIcon, FileSpreadsheetIcon, StethoscopeIcon, LockIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Specialty } from '../../types';
import { exportSpecialtiesToExcel } from '../../utils/excelExport';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../ui/Pagination';
export function Specialties() {
  const {
    specialties,
    addSpecialty,
    updateSpecialty,
    deleteSpecialty,
    getDoctorsBySpecialty
  } = useData();
  const {
    user
  } = useAuth();
  const isAdmin = user?.role === 'administrador';
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const readOnly = isReadOnly('specialties');
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedSpecialty, setSelectedSpecialty] = useState<Specialty | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isExporting, setIsExporting] = useState(false);
  const filteredSpecialties = specialties.filter(s => s.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const {
    paginatedItems: paginatedSpecialties,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage
  } = usePagination(filteredSpecialties);
  const openNewForm = () => {
    if (!canCreate('specialties')) return;
    setSelectedSpecialty(null);
    setFormData({
      name: '',
      description: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (specialty: Specialty) => {
    if (!canEdit('specialties')) return;
    setSelectedSpecialty(specialty);
    setFormData({
      name: specialty.name,
      description: specialty.description
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openDeleteConfirm = (specialty: Specialty) => {
    if (!canDelete('specialties')) return;
    setSelectedSpecialty(specialty);
    setIsDeleteConfirmOpen(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    if (selectedSpecialty) {
      updateSpecialty(selectedSpecialty.id, formData);
    } else {
      addSpecialty(formData);
    }
    setIsFormOpen(false);
  };
  const handleDelete = () => {
    if (!canDelete('specialties')) return;
    if (selectedSpecialty) {
      deleteSpecialty(selectedSpecialty.id);
      setIsDeleteConfirmOpen(false);
      setSelectedSpecialty(null);
    }
  };
  const handleExportToExcel = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportSpecialtiesToExcel(specialties);
    setIsExporting(false);
  };
  return <div className="space-y-6" data-id="element-2193">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-2194">
        <div data-id="element-2195">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-2196">
            Especialidades
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-2197">
            Gestión de especialidades médicas
            {readOnly && <span className="ml-2 text-amber-600 dark:text-amber-400" data-id="element-2198">
                (Solo lectura)
              </span>}
          </p>
        </div>
        {canCreate('specialties') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-2200" />} onClick={openNewForm} data-id="element-2199">
            Nueva Especialidad
          </Button>}
      </div>

      {/* Beautiful Export Card - Only for Admin */}
      {isAdmin && <Card className="bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-950/30 dark:to-pink-950/30 border-rose-200 dark:border-rose-800" data-id="element-2201">
          <div className="flex items-center justify-between" data-id="element-2202">
            <div className="flex items-center gap-4" data-id="element-2203">
              <div className="w-14 h-14 rounded-xl bg-rose-500 dark:bg-rose-600 flex items-center justify-center shadow-lg shadow-rose-500/20" data-id="element-2204">
                <FileSpreadsheetIcon className="w-7 h-7 text-white" data-id="element-2205" />
              </div>
              <div data-id="element-2206">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-2207">
                  Exportar Base de Datos
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-2208">
                  Descarga todas las especialidades en formato Excel
                </p>
                <div className="flex items-center gap-4 mt-2" data-id="element-2209">
                  <div className="flex items-center gap-1.5 text-sm text-rose-700 dark:text-rose-400" data-id="element-2210">
                    <StethoscopeIcon className="w-4 h-4" data-id="element-2211" />
                    <span className="font-medium" data-id="element-2212">
                      {specialties.length} especialidades
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2213">
                    • Incluye: Nombre, Descripción
                  </div>
                </div>
              </div>
            </div>
            <Button leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-2215" />} onClick={handleExportToExcel} isLoading={isExporting} className="shadow-lg shadow-rose-500/20 hover:shadow-xl hover:shadow-rose-500/30 transition-all duration-200" data-id="element-2214">
              {isExporting ? 'Exportando...' : 'Descargar Excel'}
            </Button>
          </div>
        </Card>}

      {/* Search */}
      <Card data-id="element-2216">
        <div className="relative" data-id="element-2217">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-2218" />
          <Input placeholder="Buscar especialidades..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-id="element-2219" />
        </div>
      </Card>

      {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-2220">
          <LockIcon className="w-4 h-4" data-id="element-2221" />
          <span data-id="element-2222">
            Solo tienes permisos de lectura. No puedes crear, editar o eliminar
            especialidades.
          </span>
        </div>}

      {/* Specialties Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2223">
        {paginatedSpecialties.length === 0 ? <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400" data-id="element-2224">
            No se encontraron especialidades
          </div> : paginatedSpecialties.map(specialty => {
        const doctorCount = getDoctorsBySpecialty(specialty.id).length;
        return <Card key={specialty.id} data-id="element-2225">
                <div className="flex items-start justify-between mb-3" data-id="element-2226">
                  <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-2227">
                    {specialty.name}
                  </h3>

                  {!readOnly && <div className="flex gap-1" data-id="element-2228">
                      {canEdit('specialties') && <button onClick={() => openEditForm(specialty)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" title="Editar especialidad" data-id="element-2229">
                          <EditIcon className="w-4 h-4" data-id="element-2230" />
                        </button>}

                      {canDelete('specialties') && <button onClick={() => openDeleteConfirm(specialty)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Eliminar especialidad" data-id="element-2231">
                          <TrashIcon className="w-4 h-4" data-id="element-2232" />
                        </button>}
                    </div>}
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-2233">
                  {specialty.description}
                </p>

                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-2234">
                  <UsersIcon className="w-4 h-4" data-id="element-2235" />
                  <span data-id="element-2236">
                    {doctorCount} {doctorCount === 1 ? 'doctor' : 'doctores'}
                  </span>
                </div>
              </Card>;
      })}
      </div>

      {/* Pagination */}
      <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-2237" />

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedSpecialty ? 'Editar Especialidad' : 'Nueva Especialidad'} icon={<StethoscopeIcon className="w-5 h-5" data-id="element-2239" />} accentColor="rose" data-id="element-2238">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-2240">
          {/* Specialty Info */}
          <div data-id="element-2241">
            <div className="flex items-center gap-2 mb-4" data-id="element-2242">
              <span className="w-1.5 h-5 rounded-full bg-rose-500" data-id="element-2243"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-2244">
                Información de la Especialidad
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-2245">
              <Input label="Nombre de la Especialidad" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} error={formErrors.name} placeholder="Ej: Cardiología, Pediatría..." data-id="element-2246" />
              <Textarea label="Descripción" value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} rows={3} placeholder="Descripción de la especialidad..." data-id="element-2247" />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-2248">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-2249">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-2250">
              {selectedSpecialty ? 'Guardar Cambios' : 'Crear Especialidad'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Eliminar Especialidad" data-id="element-2251">
        <div className="space-y-4" data-id="element-2252">
          <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg" data-id="element-2253">
            <AlertTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" data-id="element-2254" />
            <div data-id="element-2255">
              <p className="text-sm font-medium text-red-800 dark:text-red-200" data-id="element-2256">
                ¿Estás seguro de que deseas eliminar esta especialidad?
              </p>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1" data-id="element-2257">
                Esta acción no se puede deshacer.
              </p>
            </div>
          </div>

          {selectedSpecialty && <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg" data-id="element-2258">
              <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2259">
                {selectedSpecialty.name}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1" data-id="element-2260">
                {selectedSpecialty.description}
              </p>
              {getDoctorsBySpecialty(selectedSpecialty.id).length > 0 && <p className="text-sm text-amber-600 dark:text-amber-400 mt-2" data-id="element-2261">
                  ⚠️ Hay {getDoctorsBySpecialty(selectedSpecialty.id).length}{' '}
                  {getDoctorsBySpecialty(selectedSpecialty.id).length === 1 ? 'doctor asignado' : 'doctores asignados'}{' '}
                  a esta especialidad
                </p>}
            </div>}

          <div className="flex justify-end gap-3 pt-4" data-id="element-2262">
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-2263">
              Cancelar
            </Button>
            <Button variant="outline" onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white border-red-600" data-id="element-2264">
              Eliminar Especialidad
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}